/**
 * The Quick Start proposal tree's SELECTION and DRAG-AND-DROP, shared by both screens.
 *
 * ── WHY THIS EXISTS ────────────────────────────────────────────────────────────────────
 *
 * The owner, on the Analysis screen: "I can't select any check box nor can I drag and drop
 * those work Items across."
 *
 * He was right, and the cause was not a bug — it was an unfinished port. The Analysis screen
 * rendered the shared tree with every interactive prop wired to a stub:
 *
 *     isOn={NEVER}  setMany={NOOP}  toggleProgram={NOOP}  toggleOpp={NOOP}  toggleDel={NOOP}
 *     onItemDragStart={NOOP}  onItemDragEnd={NOOP}  onPhaseDragOver={NOOP}  onPhaseDrop={NOOP}
 *
 * so every checkbox rendered permanently unchecked and every drag was refused. The SOURCE's
 * analysis screen is fully interactive — `quick-start-analysis.ejs:191-206` carries Select
 * all, Clear all and Generate selected, and it includes `_quick-start-review`, which is where
 * the drag handlers live. The tree was cloned; the behaviour behind it was not.
 *
 * ── WHY A HOOK RATHER THAN A SECOND COPY ───────────────────────────────────────────────
 *
 * This code is COPIED VERBATIM out of QuickStartPreview, where it already worked, including
 * the two bug fixes its own comments record — the missing `dataTransfer.setData` that made
 * items draggable but not droppable, and the guard that read `dragRef.current` after clearing
 * it, so every drop returned early. Retyping that into a second screen would have thrown both
 * fixes away by hand. A hook keeps one definition of behaviour the user experiences as one
 * thing.
 *
 * Note this is NOT the "collapse two screens into one parameterised component" that
 * CLAUDE.md §1.2.1 forbids: the screens stay separate and are cloned separately. What is
 * shared is the interaction logic the source itself shares, by putting it in a partial both
 * views include.
 */
import { useCallback, useRef, useState } from 'react';
import type React from 'react';
import { pdApi } from '../api';
import {
  type Dict,
  PHASE_LABEL_FULL,
  PHASE_ORDER,
  dropKey,
  keyDel,
  keyGlobal,
  keyOpp,
  keyProg,
  keyTeam,
  s,
} from './QuickStartReviewBody';

export interface QuickStartTreeOptions {
  programs: Dict[];
  /** The live proposal — the drop handler rewrites it optimistically. */
  proposal: Dict | null;
  setProposal: (next: Dict | null) => void;
  /** The run whose order is being persisted. Null disables persistence. */
  runId: number | null;
  setError: (message: string | null) => void;
}

export function useQuickStartTree({
  programs,
  proposal,
  setProposal,
  runId,
  setError,
}: QuickStartTreeOptions) {
  const [sel, setSel] = useState<Set<string>>(new Set());

  /* ---- selection ---- */
  const isOn = (k: string) => sel.has(k);

  const setMany = (keys: string[], on: boolean) =>
    setSel((cur) => {
      const next = new Set(cur);
      for (const k of keys) {
        if (on) next.add(k);
        else next.delete(k);
      }
      return next;
    });

  const descendantsOf = useCallback(
    (pg: Dict) => {
      const pid = s(pg.ref || pg.code);
      const out = [keyProg(pid)];
      for (const o of (pg.opportunities as Dict[]) ?? []) {
        const oid = s(o.ref || o.code);
        out.push(keyOpp(pid, oid));
        const dels = ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
        dels.forEach((_d, i) => out.push(keyDel(pid, oid, String(i))));
      }
      for (const t of (pg.team as Dict[]) ?? []) out.push(keyTeam(pid, s(t.ref || t.name)));
      ((pg.global_deliverables as Dict[]) ?? []).forEach((_g, gi) =>
        out.push(keyGlobal(pid, String(gi))),
      );
      return out;
    },
    [],
  );

  const toggleProgram = (pg: Dict, on: boolean) => setMany(descendantsOf(pg), on);

  const toggleOpp = (pg: Dict, o: Dict, on: boolean) => {
    const pid = s(pg.ref || pg.code);
    const oid = s(o.ref || o.code);
    const dels = ((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[s(o.ref)] ?? [];
    const keys = [keyOpp(pid, oid), ...dels.map((_d, i) => keyDel(pid, oid, String(i)))];
    // Checking an opportunity back-fills its program; unchecking never clears upward.
    if (on) keys.push(keyProg(pid));
    setMany(keys, on);
  };

  const toggleDel = (pg: Dict, o: Dict, idx: number, on: boolean) => {
    const pid = s(pg.ref || pg.code);
    const oid = s(o.ref || o.code);
    const keys = [keyDel(pid, oid, String(idx))];
    if (on) keys.push(keyOpp(pid, oid), keyProg(pid));
    setMany(keys, on);
  };

  const dragRef = useRef<{ pid: string; oid: string | null; index: number } | null>(null);
  const [dropHint, setDropHint] = useState<string | null>(null);
  /*
   * WHERE inside the hovered phase the item lands — the array index of the row it goes BEFORE,
   * or null for the end. Without this a drag could only ever append, so an item could change
   * phase but never change position, and dropping onto the phase it already sits in did
   * nothing at all (source `quick-start.ejs:1341`, which computes the same reference row).
   */
  const [dropBefore, setDropBefore] = useState<number | null>(null);
  /*
   * `dragover` fires ~60 times a second and a programme can hold thousands of rows, so the row
   * midpoints are measured ONCE per phase per drag, not per event. The dragged row is excluded
   * from the measurement, so moving it cannot invalidate the numbers derived from it.
   */
  const rowsRef = useRef<{ el: Element; rows: { idx: number; mid: number }[] } | null>(null);
  /* ONE key builder for the hovered phase group. The handler and the two render sites derived
     this separately and disagreed on how to spell a null oid — so the programme-level groups
     never lit up, and a valid drop target looked like an invalid one. */


  /** The flat list a work item belongs to: an opportunity's, or the programme-level set. */
  const listFor = (pg: Dict, oid: string | null): Dict[] =>
    oid
      ? (((pg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[oid] ?? [])
      : ((pg.global_deliverables as Dict[]) ?? []);

  const onItemDragStart = (
    e: React.DragEvent,
    pid: string,
    oid: string | null,
    index: number,
  ) => {
    dragRef.current = { pid, oid, index };
    rowsRef.current = null;
    setDropBefore(null);
    /*
     * `setData` IS NOT OPTIONAL — this is what made the item draggable but not droppable.
     *
     * The HTML5 drag contract requires the drag to carry a payload: without a `setData` call
     * the drag starts and shows its ghost image, but no drop target will accept it, so the
     * item springs back and nothing happens. The source does exactly this
     * (`quick-start.ejs:818`) and the omission was mine.
     *
     * Wrapped in try/catch as the source wraps it — some browsers throw on `setData` outside a
     * genuine user-initiated drag, and a throw here would abort the drag entirely.
     */
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', 'wi');
    } catch {
      /* non-fatal — see above */
    }
  };
  const onItemDragEnd = () => {
    dragRef.current = null;
    rowsRef.current = null;
    setDropHint(null);
    setDropBefore(null);
  };

  /** A drop is legal only inside the SAME owner — same project, same opportunity. */
  const canDropHere = (pid: string, oid: string | null) => {
    const d = dragRef.current;
    return !!d && d.pid === pid && (d.oid ?? '') === (oid ?? '');
  };

  const onPhaseDragOver = (e: React.DragEvent, pid: string, oid: string | null, pk: string) => {
    if (!canDropHere(pid, oid)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    const hint = dropKey(pid, oid, pk);

    /* Measurements belong to ONE phase — a new phase invalidates them. */
    const grp = e.currentTarget as HTMLElement;
    if (!rowsRef.current || rowsRef.current.el !== grp) {
      const rows: { idx: number; mid: number }[] = [];
      grp.querySelectorAll<HTMLElement>('[data-qsidx]').forEach((el) => {
        const idx = Number(el.dataset.qsidx);
        /* The dragged row itself is never a reference point — `data-qsidx` is its position in
           the owner's flat list, so it identifies the row uniquely across all five phases. */
        if (idx === dragRef.current?.index) return;
        const b = el.getBoundingClientRect();
        rows.push({ idx, mid: b.top + b.height / 2 });
      });
      rowsRef.current = { el: grp, rows };
    }
    let before: number | null = null;
    for (const r of rowsRef.current.rows) {
      if (e.clientY < r.mid) {
        before = r.idx;
        break;
      }
    }
    /* Only write when the insertion point actually changes — rule 3 of the source's four. */
    if (before !== dropBefore) setDropBefore(before);
    /* Only when it actually changes — spec 6.5 rule 3. */
    if (dropHint !== hint) setDropHint(hint);
  };

  const onPhaseDrop = async (
    e: React.DragEvent,
    pid: string,
    oid: string | null,
    pk: string,
  ) => {
    /* The drop's own preventDefault, as the source has it (`quick-start.ejs:893`). Without it
       the browser runs its default action for the dropped payload — which for `text/plain` can
       mean navigating away from the page. */
    e.preventDefault();

    /*
     * THE GUARD IS EVALUATED BEFORE THE DRAG STATE IS CLEARED. This ordering was the bug that
     * made a drop do nothing at all.
     *
     * It used to read:
     *     const d = dragRef.current;
     *     onItemDragEnd();                            // sets dragRef.current = null
     *     if (!d || !canDropHere(pid, oid)) return;   // canDropHere RE-READS the null ref
     *
     * `canDropHere` resolves `dragRef.current` itself, so clearing the ref first made it
     * return false on every drop — the item dragged, the target highlighted, and then the
     * handler returned before touching anything. Capturing `d` was not enough, because the
     * guard did not use `d`.
     *
     * Now the decision is made from the captured value, and the cleanup happens after it.
     */
    const d = dragRef.current;
    const sameOwner = !!d && d.pid === pid && (d.oid ?? '') === (oid ?? '');
    const before = dropBefore; /* captured for the same reason `d` is — cleanup clears it */
    onItemDragEnd();
    if (!d || !sameOwner) return;

    const pg = programs.find((x) => s(x.ref ?? x.code) === pid);
    if (!pg) return;
    const list = listFor(pg, oid);
    const moving = list[d.index];
    if (!moving) return;

    /*
     * Rebuild the WHOLE flat order, phase by phase in canonical order, with the dragged item
     * appended to its new phase. Sending the whole list is the spec's rule: the screen shows a
     * grouped view of a flat array, and index arithmetic across that boundary is where an
     * off-by-one silently re-phases the wrong item.
     */
    const phaseOf = (item: Dict) => s(item.phase_key ?? item.phase) || 'Assessment_Planning';
    const order: { index: number; phase: string }[] = [];
    const groupKeys = [
      ...PHASE_ORDER,
      ...list.map(phaseOf).filter((k) => !PHASE_ORDER.includes(k)),
    ];
    for (const key of [...new Set(groupKeys)]) {
      list.forEach((item, i) => {
        if (i === d.index) return;
        if (phaseOf(item) === key) order.push({ index: i, phase: key });
      });
      if (key !== pk) continue;
      /*
       * Insert AT the reference row rather than appending. `before` is the flat-array index of
       * the row the pointer sits above; splicing at its position in the rebuilt list keeps the
       * two views of the same data in step. A null reference means the pointer was below every
       * row, which is the end of the phase.
       */
      if (before === null) {
        order.push({ index: d.index, phase: pk });
      } else {
        const at = order.findIndex((x) => x.index === before && x.phase === pk);
        if (at < 0) order.push({ index: d.index, phase: pk });
        else order.splice(at, 0, { index: d.index, phase: pk });
      }
    }

    /* Nothing actually moved — do not spend a round trip on a no-op. */
    const wasOrder: { index: number; phase: string }[] = [];
    for (const key of [...new Set(groupKeys)])
      list.forEach((item, i) => {
        if (phaseOf(item) === key) wasOrder.push({ index: i, phase: key });
      });
    if (
      wasOrder.length === order.length &&
      wasOrder.every((x, i) => x.index === order[i]!.index && x.phase === order[i]!.phase)
    )
      return;

    const prevProposal = proposal;
    /* Optimistic: rewrite the proposal locally so the item moves immediately. */
    const rebuilt = order.map((x) => {
      const item = { ...list[x.index]! };
      if (s(item.phase_key) !== x.phase) {
        item.phase_key = x.phase;
        item.phase_label = PHASE_LABEL_FULL[x.phase] ?? x.phase;
        item.phase_overridden = true;
      }
      return item;
    });
    const nextProposal = JSON.parse(JSON.stringify(proposal)) as Dict;
    const npg = ((nextProposal.programs as Dict[]) ?? []).find(
      (x) => s(x.ref ?? x.code) === pid,
    );
    if (npg) {
      if (oid) ((npg.deliverables_by_opp as Record<string, Dict[]>) ?? {})[oid] = rebuilt;
      else npg.global_deliverables = rebuilt;
      setProposal(nextProposal);
    }

    try {
      /* The run is passed in rather than read from a screen-local variable, which is the
         only change to this block: `runId` is null on a screen with no run, and a reorder
         with nothing to persist against must not be sent. */
      if (runId == null) return;
      await pdApi.quickStart.reorderItems(runId, pid, oid, order);
    } catch (e) {
      setProposal(prevProposal);
      setError(`Could not move that work item: ${(e as Error).message}`);
    }
  };

  /** Select or clear EVERY key in the tree — the source's `qsAll(true|false)`. */
  const selectAll = useCallback(
    (on: boolean) => {
      if (!on) {
        setSel(new Set());
        return;
      }
      const all: string[] = [];
      for (const pg of programs) all.push(...descendantsOf(pg));
      setSel(new Set(all));
    },
    [programs, descendantsOf],
  );

  return {
    sel,
    setSel,
    isOn,
    setMany,
    descendantsOf,
    toggleProgram,
    toggleOpp,
    toggleDel,
    selectAll,
    dropHint,
    dropBefore,
    onItemDragStart,
    onItemDragEnd,
    onPhaseDragOver,
    onPhaseDrop,
  };
}
